/* eslint-disable jsx-a11y/label-has-associated-control */
import { Tooltip } from 'antd'
import useMediaQuery from 'hooks/useMediaQuery'
import { CSSProperties, ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { FiFilePlus } from 'react-icons/fi'
import { MOBILE_WIDTH } from 'utils/constants'
import { generateRandomNumber } from 'utils/randomNumberGenerator'
import * as Styles from './styles'

interface FileInputProps {
  label: string
  description?: string | ReactNode
  onChange: (file?: File) => void
  errorMessage?: string
  value?: string
  url?: string
  width?: string
  maxWidth?: string
  style?: CSSProperties
}

export default function FileInput({
  label,
  description,
  onChange,
  value,
  url,
  width,
  maxWidth,
  style,
  errorMessage
}: FileInputProps) {
  const isMobile = useMediaQuery(MOBILE_WIDTH)
  const containerRef = useRef<HTMLDivElement>(null)
  const invisibleInputId = useMemo(() => generateRandomNumber(), [])
  const [fakeInputValue, setFakeInputValue] = useState('')

  const onFileChange = (files?: FileList | null) => {
    if (!files) {
      onChange(undefined)
      return
    }
    onChange(files[0] ?? undefined)
  }

  const defineFakeFileInputValue = () => {
    const fakeInputWidth = containerRef.current?.getBoundingClientRect().width ?? 0
    const quantityOfCharactersThatFit = Math.floor(fakeInputWidth / (isMobile ? 12 : 14))

    if (!value) {
      setFakeInputValue('')
      return
    }
    if (value?.length > quantityOfCharactersThatFit) {
      setFakeInputValue(`${value.substring(0, quantityOfCharactersThatFit)}...`)
      return
    }
    setFakeInputValue(value)
  }

  useEffect(() => {
    defineFakeFileInputValue()
  }, [maxWidth, value, isMobile])

  return (
    <Styles.FileInputContainer ref={containerRef} style={{ width, maxWidth, ...style }}>
      <Styles.TitleAndDescriptionWrapper>
        <label>{label}</label>
        {description && <p>{description}</p>}
      </Styles.TitleAndDescriptionWrapper>

      <Styles.InvisibleFileInput
        type="file"
        id={invisibleInputId}
        onChange={(event) => onFileChange(event.target.files)}
      />
      <Styles.FakeFileInput htmlFor={invisibleInputId} hasError={errorMessage !== undefined && errorMessage.length > 0}>
        <Tooltip title={value ?? ''} trigger={isMobile ? 'click' : 'hover'} placement="top" style={{ zIndex: 1100 }}>
          {url ? (
            <a href={url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
              {fakeInputValue}
            </a>
          ) : (
            <span>{fakeInputValue}</span>
          )}
        </Tooltip>
        <FiFilePlus color={!value ? '#21976E' : '#3d3d3d'} />
      </Styles.FakeFileInput>

      {errorMessage !== undefined && errorMessage.length > 0 && (
        <Styles.FileInputErrorMessage>{errorMessage}</Styles.FileInputErrorMessage>
      )}
    </Styles.FileInputContainer>
  )
}
